import React from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ResponsiveFiltersPanel from '../admin/ResponsiveFiltersPanel';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

const fieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

/**
 * @param {{
 *   subtitle?: React.ReactNode;
 *   extraActions?: React.ReactNode;
 *   collapsed: boolean;
 *   searchTerm: string;
 *   selectedPriority: string;
 *   selectedStartDate: string;
 *   selectedEndDate: string;
 *   priorityOptions: string[];
 *   onToggleCollapsed: () => void;
 *   onSearchChange: (value: string) => void;
 *   onPriorityChange: (value: string) => void;
 *   onStartDateChange: (value: string) => void;
 *   onEndDateChange: (value: string) => void;
 * }} props
 */
export default function SupportFilters({
  subtitle,
  extraActions,
  collapsed,
  searchTerm,
  selectedPriority,
  selectedStartDate,
  selectedEndDate,
  priorityOptions,
  onToggleCollapsed,
  onSearchChange,
  onPriorityChange,
  onStartDateChange,
  onEndDateChange,
}) {
  const advancedCount = [
    searchTerm || null,
    selectedPriority && selectedPriority !== 'Todas' ? selectedPriority : null,
    selectedStartDate || null,
    selectedEndDate || null,
  ].filter(Boolean).length;

  function handleReset() {
    onSearchChange('');
    onPriorityChange('Todas');
    onStartDateChange('');
    onEndDateChange('');
  }

  return (
    <Box sx={{ mb: 2.5 }}>
      <ResponsiveFiltersPanel
        subtitle={subtitle}
        activeFiltersCount={advancedCount}
        onClear={handleReset}
        filtersOpen={!collapsed}
        onToggleFilters={onToggleCollapsed}
        actions={extraActions}
        mobileDrawerTitle="Filtros de suporte"
      >
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'block', sm: collapsed ? 'none' : 'block' },
            backgroundColor: '#fff',
            p: 2.5,
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0',
          }}
        >
            <Grid container spacing={1.5} alignItems="flex-end">
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Pesquise por chave, título, status..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#bbb', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY } }}
                />
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  select
                  label="Prioridade"
                  size="small"
                  fullWidth
                  value={selectedPriority}
                  onChange={(e) => onPriorityChange(e.target.value)}
                  sx={fieldSx}
                >
                  {priorityOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="De"
                  type="date"
                  size="small"
                  fullWidth
                  value={selectedStartDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Até"
                  type="date"
                  size="small"
                  fullWidth
                  value={selectedEndDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={handleReset}
                    sx={{ color: '#888', textTransform: 'none' }}
                  >
                    Resetar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onToggleCollapsed}
                    sx={{
                      backgroundColor: PRIMARY,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: PRIMARY_DARK, boxShadow: 'none' },
                    }}
                  >
                    Fechar
                  </Button>
                </Box>
              </Grid>
            </Grid>
        </Paper>
      </ResponsiveFiltersPanel>
    </Box>
  );
}
